import React, { useState, useEffect } from "react";
import { 
  DollarSign, TrendingUp, Users, Calendar, Download, 
  RefreshCw, Filter, X, ChevronDown 
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import axiosInstance from "../../utils/axiosInstance";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";

const RevenueManager = () => {
  const [loading, setLoading] = useState(false);
  const [revenueData, setRevenueData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedCardFilter, setSelectedCardFilter] = useState("all"); // New state for card filter
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  const filterOptions = [
    { id: "all", label: "📊 All Time", icon: "📊" },
    { id: "today", label: "📅 Today", icon: "📅" },
    { id: "last24h", label: "⏰ Last 24 Hours", icon: "⏰" },
    { id: "thisMonth", label: "📆 This Month", icon: "📆" },
    { id: "quarter", label: "📈 This Quarter", icon: "📈" },
    { id: "thisYear", label: "🗓️ This Year", icon: "🗓️" },
    { id: "custom", label: "🎯 Custom Range", icon: "🎯" },
  ];

  useEffect(() => {
    fetchRevenueData();
  }, [selectedFilter, customStartDate, customEndDate]);

  // Filter transactions when card is clicked or transactions change
  useEffect(() => {
    if (!transactions || transactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }

    let filtered = [...transactions];

    // Sort by latest first (newest on top)
    filtered.sort((a, b) => {
      const dateA = Array.isArray(a.createdAt) 
        ? new Date(a.createdAt[0], a.createdAt[1] - 1, a.createdAt[2], a.createdAt[3] || 0, a.createdAt[4] || 0, a.createdAt[5] || 0)
        : new Date(a.createdAt);
      const dateB = Array.isArray(b.createdAt)
        ? new Date(b.createdAt[0], b.createdAt[1] - 1, b.createdAt[2], b.createdAt[3] || 0, b.createdAt[4] || 0, b.createdAt[5] || 0)
        : new Date(b.createdAt);
      return dateB - dateA; // Descending order (latest first)
    });

    // Apply card filter
    if (selectedCardFilter !== "all") {
      filtered = filtered.filter(t => {
        switch (selectedCardFilter) {
          case "new":
            return t.transactionType === "NEW_SUBSCRIPTION";
          case "renewal":
            return t.transactionType === "RENEWAL";
          case "plan_change":
            return t.transactionType === "PLAN_CHANGE";
          default:
            return true;
        }
      });
    }

    setFilteredTransactions(filtered);
  }, [transactions, selectedCardFilter]);

  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      const params = { filter: selectedFilter };
      
      if (selectedFilter === "custom" && customStartDate && customEndDate) {
        params.startDate = customStartDate.toISOString().split('T')[0];
        params.endDate = customEndDate.toISOString().split('T')[0];
      }

      const response = await axiosInstance.get("/revenue/summary", { params });
      setRevenueData(response.data.responseData);
      setTransactions(response.data.responseData.transactions || []);
      setSelectedCardFilter("all"); // Reset card filter when data changes
    } catch (error) {
      console.error("Error fetching revenue data:", error);
      toast.error("Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterId) => {
    setSelectedFilter(filterId);
    if (filterId !== "custom") {
      setShowCustomRange(false);
      setCustomStartDate(null);
      setCustomEndDate(null);
    } else {
      setShowCustomRange(true);
    }
  };

  const handleCardClick = (filterType) => {
    setSelectedCardFilter(selectedCardFilter === filterType ? "all" : filterType);
  };

  const handleExportExcel = () => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      toast.error("No data to export");
      return;
    }

    const formatDate = (dateValue) => {
      try {
        if (!dateValue) return "N/A";
        
        // Handle array format [year, month, day, hour, minute, second, nano]
        if (Array.isArray(dateValue)) {
          const [year, month, day, hour = 0, minute = 0, second = 0] = dateValue;
          const date = new Date(year, month - 1, day, hour, minute, second);
          return date.toLocaleString("en-IN", {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        }
        
        // Handle string format
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return "N/A";
        
        return date.toLocaleString("en-IN", {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      } catch (error) {
        console.error('Date formatting error:', error);
        return "N/A";
      }
    };

    const exportData = filteredTransactions.map((t) => ({
      "Transaction ID": t.paymentId || t.razorpayPaymentId || "N/A",
      "Razorpay Order ID": t.razorpayOrderId || "N/A",
      "User Name": t.userName || "N/A",
      "User Email": t.userEmail || "N/A",
      "Type": t.transactionType || "N/A",
      "Plan Name": t.planName || "N/A",
      "Package Name": t.packageName || "N/A",
      "Amount": t.amount ? `₹${t.amount}` : "₹0",
      "Discount": t.discount ? `₹${t.discount}` : "₹0",
      "Final Amount": t.finalAmount ? `₹${t.finalAmount}` : "₹0",
      "Coupon Used": t.couponCode || "No",
      "Payment Method": t.paymentMethod || "Razorpay",
      "Status": t.status || "N/A",
      "Date": formatDate(t.createdAt),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Revenue");
    
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    
    const filterLabel = filterOptions.find(f => f.id === selectedFilter)?.label || "All Time";
    saveAs(blob, `Revenue_Report_${filterLabel.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Excel file downloaded successfully!");
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, filterType, isActive }) => (
    <div 
      onClick={() => filterType && handleCardClick(filterType)}
      className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border-2 transition-all ${
        isActive 
          ? 'border-orange-500 shadow-lg ring-2 ring-orange-200 dark:ring-orange-900' 
          : 'border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-orange-300'
      } ${filterType ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend && (
          <span className={`text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{value}</p>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
      {filterType && isActive && (
        <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 font-medium">
          ✓ Filtered
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Revenue Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Track and analyze subscription revenue and transactions</p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
              <Filter className="w-5 h-5" />
              <span className="font-medium">Filter:</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {filterOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleFilterChange(option.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedFilter === option.id
                      ? "bg-orange-500 text-white shadow-md"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <button
              onClick={fetchRevenueData}
              disabled={loading}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Custom Date Range */}
          {showCustomRange && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">From:</label>
                  <DatePicker
                    selected={customStartDate}
                    onChange={(date) => setCustomStartDate(date)}
                    selectsStart
                    startDate={customStartDate}
                    endDate={customEndDate}
                    maxDate={customEndDate || new Date()}
                    dateFormat="dd MMM yyyy"
                    placeholderText="Start date"
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">To:</label>
                  <DatePicker
                    selected={customEndDate}
                    onChange={(date) => setCustomEndDate(date)}
                    selectsEnd
                    startDate={customStartDate}
                    endDate={customEndDate}
                    minDate={customStartDate}
                    maxDate={new Date()}
                    dateFormat="dd MMM yyyy"
                    placeholderText="End date"
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                {customStartDate && customEndDate && (
                  <button
                    onClick={() => {
                      setCustomStartDate(null);
                      setCustomEndDate(null);
                    }}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 animate-pulse">
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : revenueData ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <StatCard
              title="Total Revenue"
              value={`₹${revenueData.totalRevenue?.toLocaleString('en-IN') || 0}`}
              subtitle={`From ${revenueData.transactionCount || 0} transactions`}
              icon={DollarSign}
              color="bg-gradient-to-br from-green-500 to-green-600"
              trend={revenueData.revenueTrend}
              filterType={null}
              isActive={false}
            />
            <StatCard
              title="New Subscriptions"
              value={revenueData.newSubscriptions || 0}
              subtitle="First-time subscribers"
              icon={Users}
              color="bg-gradient-to-br from-blue-500 to-blue-600"
              filterType="new"
              isActive={selectedCardFilter === "new"}
            />
            <StatCard
              title="Renewals"
              value={revenueData.renewals || 0}
              subtitle="Subscription renewals"
              icon={RefreshCw}
              color="bg-gradient-to-br from-purple-500 to-purple-600"
              filterType="renewal"
              isActive={selectedCardFilter === "renewal"}
            />
            <StatCard
              title="Plan Changes"
              value={revenueData.planChanges || 0}
              subtitle="Plan upgrades/changes"
              icon={TrendingUp}
              color="bg-gradient-to-br from-orange-500 to-orange-600"
              filterType="plan_change"
              isActive={selectedCardFilter === "plan_change"}
            />
          </div>
        ) : null}

        {/* Transactions Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Transactions</h2>
              {selectedCardFilter !== "all" && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Showing {filteredTransactions.length} {selectedCardFilter === "new" ? "New Subscriptions" : selectedCardFilter === "renewal" ? "Renewals" : "Plan Changes"}
                  {' '}
                  <button
                    onClick={() => setSelectedCardFilter("all")}
                    className="text-orange-600 hover:text-orange-700 underline"
                  >
                    Clear filter
                  </button>
                </p>
              )}
            </div>
            <button
              onClick={handleExportExcel}
              disabled={!filteredTransactions || filteredTransactions.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">Loading transactions...</p>
              </div>
            ) : filteredTransactions && filteredTransactions.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Plan/Package</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredTransactions.map((transaction, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {(() => {
                          try {
                            if (!transaction.createdAt) return 'N/A';
                            
                            // Handle array format [year, month, day, hour, minute, second, nano]
                            if (Array.isArray(transaction.createdAt)) {
                              const [year, month, day, hour = 0, minute = 0, second = 0] = transaction.createdAt;
                              const date = new Date(year, month - 1, day, hour, minute, second);
                              return date.toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            }
                            
                            // Handle string format
                            const date = new Date(transaction.createdAt);
                            if (isNaN(date.getTime())) return 'N/A';
                            
                            return date.toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          } catch (error) {
                            console.error('Date parsing error:', error, transaction.createdAt);
                            return 'N/A';
                          }
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{transaction.userName}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{transaction.userEmail}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          transaction.transactionType === 'NEW_SUBSCRIPTION' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          transaction.transactionType === 'RENEWAL' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                          'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                        }`}>
                          {transaction.transactionType?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{transaction.planName || 'N/A'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{transaction.packageName || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">₹{transaction.finalAmount}</div>
                        {transaction.discount > 0 && (
                          <div className="text-xs text-green-600 dark:text-green-400">-₹{transaction.discount} discount</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          transaction.status === 'SUCCESS' || transaction.status === 'COMPLETED' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                            : transaction.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {transaction.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-12 text-center">
                <p className="text-gray-500 dark:text-gray-400">No transactions found for the selected period</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueManager;